import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ErrorObject } from 'src/app/_models/error';
import { FormBase } from 'src/app/_models/form-base';
import { Laboratory } from 'src/app/_models/laboratory';
import { LicenseDisplay } from 'src/app/_models/license-type';
import {
  OrganisationIndexView,
  OrganisationOverviewIndex,
} from 'src/app/_models/organisation';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { ModalService } from 'src/app/_services/modal.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { OrganisationService } from 'src/app/_services/organisation.service';
import { ModalComponent } from '../../_components/modals/modal.component';
import { DropdownFormFieldComponent } from '../../_components/forms/dropdown-form-field/dropdown-form-field.component';
import { MatIcon } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { LoadingSpinnerComponent } from '../../_components/loading-spinner/loading-spinner.component';
import { IconButtonComponent } from '../../_components/buttons/icon-button/icon-button.component';
import { SearchFieldComponent } from 'src/app/_components/forms/search-field/search-field.component';
import { SumErrorLogsPipe } from 'src/app/_helpers/sum-error-logs.pipe';
import { DeviceService } from 'src/app/_services/device.service';
import { ErrorLogService } from 'src/app/_services/error-log.service';
import { ExperimentService } from 'src/app/_services/experiment.service';
import { update } from '@intercom/messenger-js-sdk';
import { ErrorPageComponent } from 'src/app/_components/error-page/error-page.component';
import { ReducedSmallDataCardComponent } from 'src/app/_components/cards/reduced-small-data-card/reduced-small-data-card.component';
import { MatOutlinedButtonComponent } from 'src/app/_components/buttons/mat-outlined-button/mat-outlined-button.component';
import {
  InformationBanner,
  InformationBannerType,
} from 'src/app/_models/information-banner';
import { InfoBannerModalComponent } from 'src/app/_components/modals/info-banner-modal/info-banner-modal.component';

@Component({
  selector: 'app-overview',
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.scss'],
  imports: [
    IconButtonComponent,
    LoadingSpinnerComponent,
    MatIcon,
    DropdownFormFieldComponent,
    ModalComponent,
    SearchFieldComponent,
    SumErrorLogsPipe,
    ErrorPageComponent,
    ReducedSmallDataCardComponent,
    MatOutlinedButtonComponent,
    MatTableModule,
    InfoBannerModalComponent,
  ],
})
export class OverviewComponent implements OnInit, OnDestroy {
  private readonly formBuilder = inject(FormBuilder);
  private readonly organisationService = inject(OrganisationService);
  private readonly deviceService = inject(DeviceService);
  private readonly experimentService = inject(ExperimentService);
  private readonly errorLogService = inject(ErrorLogService);
  private readonly router = inject(Router);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly modalService = inject(ModalService);
  private readonly unsubscribe$ = new Subject<void>();

  // Permissions
  laboratory: Laboratory;
  isFirstLoad = true;
  error: ErrorObject = null;
  isProcessing = false;
  updateInformationBannerError = '';
  updateInformationBannerSuccessMessage = '';
  organisations: OrganisationIndexView[];
  selectedOrganisation: OrganisationIndexView;
  checkboxesInitialised = false;
  selectedLaboratory: Laboratory;
  currentUser = this.authenticationService.currentUserValue;
  readonly displayedColumns = [
    'name',
    'licenseType',
    'twoFactor',
    'users',
    'superUser',
    'devices',
    'labs',
    'errors',
    'actions',
  ];

  // Make the enum available to the template
  InformationBannerType = InformationBannerType;
  LicenseDisplay = LicenseDisplay;

  // Stats
  devicesOnline: string;
  devicesOnlineLast24Hours: string;
  activeExperiments: string;

  // Table control variables
  currentFilters = [];
  currentSort: {
    sortBy: string;
    sortDirection: string;
  } = {
    sortBy: 'Errors',
    sortDirection: 'desc',
  };

  // Pagination
  pageRequested = 1;
  pageSize = 100;
  totalCount = 0;
  totalPages = 0;
  currentPage = 1;
  hasNext = false;
  hasPrevious = false;

  searchField = new FormBase<string>({
    key: 'search',
    label: 'Search',
    type: 'text',
    placeholder: 'Search...',
    disabled: false,
    required: false,
    value: '',
    options: [],
  });

  orgsPerPageField = new FormBase({
    key: 'orgsPerPage',
    label: 'Organisations Per Page',
    type: 'number',
    placeholder: 'Organisations Per Page',
    disabled: false,
    required: true,
    value: '100',
    options: [
      { key: '5', value: '5' },
      { key: '10', value: '10' },
      { key: '25', value: '25' },
      { key: '50', value: '50' },
      { key: '100', value: '100' },
    ],
  });

  displayInformationBannerField = new FormBase<boolean>({
    key: 'displayInformationBanner',
    label: 'Display Information Banner',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: true,
    value: true,
    options: [],
  });

  informationBannerField = new FormBase<string>({
    key: 'informationBanner',
    label: 'Information Banner',
    type: 'text',
    placeholder: 'Enter banner message...',
    disabled: false,
    required: false,
    value: null,
    options: [],
  });

  informationBannerTypeField = new FormBase({
    key: 'informationBannerType',
    label: 'Banner Type',
    type: 'dropdown',
    placeholder: 'Select banner type...',
    disabled: false,
    required: false,
    value: null,
    options: [
      { key: InformationBannerType.Information, value: 'Information' },
      { key: InformationBannerType.Positive, value: 'Positive' },
      { key: InformationBannerType.Warning, value: 'Warning' },
      { key: InformationBannerType.Critical, value: 'Critical' },
    ],
  });

  orgTableControls = this.formBuilder.group({
    [this.searchField.key]: new FormControl(this.searchField.value),
    [this.orgsPerPageField.key]: new FormControl(this.orgsPerPageField.value),
  });

  dashboardBannerForm = this.formBuilder.group({
    displayInformationBanner: new FormControl(
      this.displayInformationBannerField.value,
    ),
    informationBanner: new FormControl(
      this.informationBannerField.value,
      [Validators.minLength(1), Validators.maxLength(150)],
    ),
    informationBannerType: new FormControl(
      this.informationBannerTypeField.value,
    ),
  });

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnInit(): void {
    // Check for saved sort, filter, and pagination settings
    if (this.organisationService.paginationCache !== null) {
      this.pageRequested = this.organisationService.paginationCache.pageNumber;
      this.pageSize = this.organisationService.paginationCache.pageSize;
      this.orgTableControls.patchValue({
        [this.orgsPerPageField.key]: this.pageSize.toString(),
      });
    }

    this.laboratory = this.authenticationService.selectedLaboratory;

    this.initialiseSubscriptions();
    this.getOrganisations();
  }

  initialiseSubscriptions(): void {
    // Subscribe to changes in the orgs per page dropdown
    this.orgTableControls
      .get(this.orgsPerPageField.key)
      ?.valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((numOfUsers) => {
        this.pageSize = numOfUsers as number;
        this.pageRequested = 1;
        this.getOrganisations();
      });

    // Subscribe to changes in the display information banner toggle
    this.dashboardBannerForm
      .get(this.displayInformationBannerField.key)
      ?.valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((isDisplayed: boolean) => {
        if (isDisplayed) {
          this.enableBannerFields();
        } else {
          this.disableBannerFields();
        }
      });
  }

  enableBannerFields(): void {
    const bannerCtrl = this.dashboardBannerForm.get(
      this.informationBannerField.key,
    );
    const typeCtrl = this.dashboardBannerForm.get(
      this.informationBannerTypeField.key,
    );

    bannerCtrl.enable();
    typeCtrl.enable();

    bannerCtrl.setValidators([
      Validators.required,
      Validators.minLength(1),
      Validators.maxLength(150),
    ]);
    typeCtrl.setValidators([Validators.required]);

    bannerCtrl.updateValueAndValidity();
    typeCtrl.updateValueAndValidity();
  }

  disableBannerFields(): void {
    const bannerCtrl = this.dashboardBannerForm.get(
      this.informationBannerField.key,
    );
    const typeCtrl = this.dashboardBannerForm.get(
      this.informationBannerTypeField.key,
    );

    bannerCtrl.disable();
    typeCtrl.disable();

    bannerCtrl.clearValidators();
    typeCtrl.clearValidators();

    this.dashboardBannerForm.patchValue({
      informationBanner: null,
      informationBannerType: null,
    });

    bannerCtrl.updateValueAndValidity();
    typeCtrl.updateValueAndValidity();
  }

  getOrganisations(): void {
    const searchValue = this.orgTableControls.controls['search']
      .value as string;

    this.organisationService
      .getOrganisations(
        this.currentSort.sortBy,
        this.currentSort.sortDirection,
        this.pageRequested,
        this.pageSize,
        typeof searchValue === 'string' ? searchValue : null,
      )
      .subscribe({
        next: (response: OrganisationOverviewIndex) => {
          this.organisations = response.data.organisations;
          this.devicesOnline =
            response.data.devicesOnline === -1
              ? 'Fetch failed'
              : response.data.devicesOnline.toString();
          this.devicesOnlineLast24Hours =
            response.data.devicesOnlineLast24Hours === -1
              ? 'Fetch failed'
              : response.data.devicesOnlineLast24Hours.toString();
          this.activeExperiments =
            response.data.activeExperiments === -1
              ? 'Fetch failed'
              : response.data.activeExperiments.toString();
          this.totalCount = response.paginationData.TotalCount;
          this.totalPages = response.paginationData.TotalPages;
          this.currentPage = response.paginationData.CurrentPage;
          this.hasNext = response.paginationData.HasNext;
          this.hasPrevious = response.paginationData.HasPrevious;
          this.isFirstLoad = false;
        },
        error: (error: ErrorObject) => {
          this.error = error;
          this.isFirstLoad = false;
        },
      });
  }

  handleSearch(action: string): void {
    if (action === 'clear-search') {
      this.orgTableControls.patchValue({ search: null });
    }
    this.getOrganisations();
  }

  getClasses(sortTerm: string): string[] {
    const active = this.currentSort.sortBy === sortTerm ? 'active' : '';
    return [active];
  }

  onSort(sortTerm: string): void {
    if (this.currentSort.sortBy === sortTerm) {
      if (this.currentSort.sortDirection === 'asc') {
        this.currentSort.sortDirection = 'desc';
      } else {
        this.currentSort.sortDirection = 'asc';
      }
    } else {
      this.currentSort.sortBy = sortTerm;
      this.currentSort.sortDirection = 'asc';
    }
    this.getOrganisations();
  }

  getAriaSort(sortTerm: string): 'ascending' | 'descending' | 'none' {
    if (this.currentSort.sortBy !== sortTerm) {
      return 'none';
    }
    return this.currentSort.sortDirection === 'asc' ? 'ascending' : 'descending';
  }

  viewOrgDetails(id: number): void {
    this.selectedOrganisation = this.organisations.find((org) => org.id === id);
  }

  getPrevious(): void {
    this.pageRequested = this.currentPage - 1;
    this.getOrganisations();
  }

  getNext(): void {
    this.pageRequested = this.currentPage + 1;
    this.getOrganisations();
  }

  onCreateNewOrgansation(): void {
    void this.router.navigate(['organisations/create-organisation']);
  }

  viewOrganisation(org: OrganisationIndexView): void {
    if (!this.currentUser) return;
    this.clearDashboardCache();
    if (org.id === this.currentUser.organisationId) {
      this.navigateToOrganisation(org);
    } else {
      this.selectedOrganisation = org;
      this.openModal('attention-org');
    }
  }

  private navigateToOrganisation(org: OrganisationIndexView): void {
    const firstLab = org.labSummary?.[0] || null;
    this.authenticationService.setCurrentOrg(org.id);
    this.authenticationService.setSelectedLaboratory(firstLab);
    // Restore Intercom to the user's own org/lab context
    update({
      laboratory: firstLab?.laboratoryName,
      organisation: org.organisationName,
    });
    void this.router.navigate([`organisations/${org.id}/details`]);
  }

  submitInformationBannerChanges(): void {
    // If form is already being submitted, exit
    if (this.isProcessing) {
      return;
    }

    this.dashboardBannerForm.markAllAsTouched();
    if (!this.dashboardBannerForm.valid) {
      return;
    }

    this.updateInformationBannerError = null;
    this.updateInformationBannerSuccessMessage = null;
    this.isProcessing = true;

    const informationBanner: InformationBanner = {
      displayInformationBanner:
        this.dashboardBannerForm.controls.displayInformationBanner.value,
      informationBannerMessage:
        this.dashboardBannerForm.controls.informationBanner.value,
      informationBannerType:
        this.dashboardBannerForm.controls.informationBannerType.value,
    };

    this.organisationService
      .updateInformationBanner(informationBanner)
      .subscribe({
        next: (banner: InformationBanner) => {
          this.isProcessing = false;
          if (banner.displayInformationBanner) {
            this.updateInformationBannerSuccessMessage =
              'Information banner updated';
            this.dashboardBannerForm.markAsPristine();
          } else {
            this.updateInformationBannerSuccessMessage =
              'Information banner turned off';
          }
        },
        error: (error: ErrorObject) => {
          this.isProcessing = false;
          this.updateInformationBannerError = error.message;
        },
      });
  }

  openModal(modalId: string): void {
    this.modalService.open(modalId);
  }

  closeModal(modalId: string): void {
    this.modalService.close(modalId);
  }

  modalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'cancel-lab-button':
        this.closeModal('attention-lab');
        break;
      case 'cancel-org-button':
        this.closeModal('attention-org');
        break;
      case 'confirm-lab-navigation':
        this.organisations.forEach((org) => {
          org.labSummary.forEach((lab) => {
            if (lab.laboratoryId === this.selectedLaboratory.laboratoryId) {
              this.authenticationService.setCurrentOrg(org.id);
            }
          });
        });
        this.authenticationService.setSelectedLaboratory(
          this.selectedLaboratory,
        );
        // Update Intercom with new lab context
        update({
          laboratory: this.selectedLaboratory?.laboratoryName,
        });
        void this.router.navigate(['laboratories/view-lab']);
        this.closeModal('attention-lab');
        break;
      case 'confirm-org-navigation':
        this.navigateToOrganisation(this.selectedOrganisation);
        break;
      case 'close-button':
        this.dashboardBannerForm.reset({
          displayInformationBanner: this.displayInformationBannerField.value,
          informationBanner: this.informationBannerField.value,
          informationBannerType: this.informationBannerTypeField.value,
        });
        this.updateInformationBannerError = null;
        this.updateInformationBannerSuccessMessage = null;
        this.closeModal('info-banner');
        break;
      case 'update-button':
        this.submitInformationBannerChanges();
        break;
    }
  }

  onViewLab(laboratory: Laboratory): void {
    if (!this.currentUser) return;
    this.clearDashboardCache();
    const isLabInOwnOrg = this.currentUser.laboratories?.find(
      (x) => x.laboratoryId === laboratory.laboratoryId,
    );

    if (isLabInOwnOrg) {
      this.authenticationService.setSelectedLaboratory(laboratory);
      void this.router.navigate(['laboratories/view-lab']);
    } else {
      this.selectedLaboratory = laboratory;
      this.openModal('attention-lab');
    }
  }

  clearDashboardCache(): void {
    // Clear device/experiment/error log table caches when org/lab context changes
    this.deviceService.onUpdatePageSize(null);
    this.experimentService.onUpdatePageSize(null);
    this.errorLogService.onUpdatePageSize(null);
  }
}
